import { defineStore } from 'pinia';
import { mainAxios as axios } from '@/assets/js/axios_config';
import { useUserStore } from '@/stores/user';
import { useExpertStore } from '@/stores/expert';
import { defaultErrorHandling } from '@/assets/js/utils';

// Define the required role values for each workspace feature
export const WORKSPACE_ROLES = {
    VIEWER: 1,
    MEMBER: 2,
    ADMIN: 3,
    OWNER: 4,
};

export const WORKSPACE_ROLES_STRING = {
    VIEWER: 'viewer',
    MEMBER: 'member',
    ADMIN: 'admin',
    OWNER: 'owner',
};

export const ROLE_TO_WORKSPACE_ROLES = {
    viewer: 'VIEWER',
    member: 'MEMBER',
    admin: 'ADMIN',
    owner: 'OWNER',
};

export const WORKSPACE_SETTINGS = {
    settings: {
        requiredRole: WORKSPACE_ROLES.OWNER,
    },
    user: {
        requiredRole: WORKSPACE_ROLES.MEMBER,
    },
    analytics: {
        requiredRole: WORKSPACE_ROLES.OWNER,
        usageRestricted: true,
    },
    widgets: {
        requiredRole: WORKSPACE_ROLES.ADMIN,
    },
    apikey: {
        requiredRole: WORKSPACE_ROLES.ADMIN,
    },
};

export const WORKSPACE_BASE_FEATURES = {
    search: {
        requiredRole: WORKSPACE_ROLES.VIEWER,
        name: 'home',
    },
    agent: {
        requiredRole: WORKSPACE_ROLES.MEMBER,
        name: 'agent',
    },
    workflows: {
        requiredRole: WORKSPACE_ROLES.MEMBER,
        name: 'workflows',
    },
};

// helper function to filter features by role
function filterFeaturesByRole(featuresObject) {
    const userStore = useUserStore();
    const role = userStore.selected_workspace_value || WORKSPACE_ROLES.VIEWER;
    const usageRestrictionValue = userStore.usage_restriction_value;

    return Object.entries(featuresObject).reduce((acc, [key, feature]) => {
        const requiredRole = feature.usageRestricted ? usageRestrictionValue : feature.requiredRole;

        if (role >= requiredRole) {
            acc[key] = feature;
        }
        return acc;
    }, {});
}

export const useWorkspaceStore = defineStore('workspace', {
    state: () => ({
        currentWorkspace: null,
        loading: false,
        changed: false,
        usageRestrictedFeatures: ['analytics'],
    }),

    getters: {
        current_user_role: (state) => state['currentWorkspace']?.user_role,

        createdByCurrentUser: (state) => {
            const userStore = useUserStore();
            return state.currentWorkspace?.created_user?.id === userStore.get_user_id;
        },

        workspace_users: (state) => {
            return state.currentWorkspace?.workspace_users;
        },

        // Get available settings based on user's role
        availableSettings: (state) => {
            return filterFeaturesByRole(WORKSPACE_SETTINGS);
        },

        // Get available base features based on user's role
        availableBaseFeatures: (state) => {
            return filterFeaturesByRole(WORKSPACE_BASE_FEATURES);
        },

        // Groups created by the current user
        created_groups: (state) => {
            const userStore = useUserStore();
            const currentUserId = userStore.get_user_id;

            if (!state.currentWorkspace?.workspace_groups) {
                return [];
            }

            return state.currentWorkspace.workspace_groups.filter(
                (group) => group.created_user_id === currentUserId,
            );
        },

        // Groups where current user is a member but not the creator
        joined_groups: (state) => {
            const userStore = useUserStore();
            const currentUserId = userStore.get_user_id;

            if (!state.currentWorkspace?.workspace_groups) {
                return [];
            }

            return state.currentWorkspace.workspace_groups.filter(
                (group) => group.created_user_id != currentUserId,
            );
        },
    },

    actions: {
        async refreshWorkspace(config = { disableLoading: false }) {
            if (!config.disableLoading) {
                this.loading = true;
                this.currentWorkspace = null;
            }
            try {
                const response = await axios.get('/workspace/user/my-workspace');
                this.currentWorkspace = response.data;
            } catch (error) {
                console.error('Failed to fetch workspace:', error);
            } finally {
                this.loading = false;
            }
        },

        async handleWorkspaceChange() {
            const userStore = useUserStore();
            const expertStore = useExpertStore();

            await Promise.all([
                userStore.loadData(),
                expertStore.loadData(),
                this.refreshWorkspace(),
            ]);
        },

        setChangedStatus(status) {
            this.changed = status;
            // Reset after 3 seconds
            setTimeout(() => {
                this.changed = false;
            }, 3000);
        },

        hasAccess(feature) {
            return feature in this.availableFeatures;
        },

        async inviteUserToGroup(groupId, email) {
            try {
                const response = await axios.post('/groups/invite_user', {
                    group_id: groupId,
                    email: email,
                });

                // Update store data - add invitation to the right group
                if (this.currentWorkspace?.workspace_groups) {
                    const groupIndex = this.currentWorkspace.workspace_groups.findIndex(
                        (group) => group.id === groupId,
                    );

                    if (groupIndex !== -1) {
                        // If no user_invitations array exists, create it
                        if (!this.currentWorkspace.workspace_groups[groupIndex].user_invitations) {
                            this.currentWorkspace.workspace_groups[groupIndex].user_invitations =
                                [];
                        }

                        // Add new invitation
                        this.currentWorkspace.workspace_groups[groupIndex].user_invitations.push(
                            response.data,
                        );
                    }
                }

                return response;
            } catch (error) {
                throw error;
            }
        },

        async deleteGroup(groupId) {
            try {
                const response = await axios.delete(`/groups/${groupId}`);

                // Update store data - remove the group
                if (this.currentWorkspace?.workspace_groups) {
                    this.currentWorkspace.workspace_groups =
                        this.currentWorkspace.workspace_groups.filter(
                            (group) => group.id !== groupId,
                        );
                }

                return response;
            } catch (error) {
                throw error;
            }
        },

        async removeUserFromGroup(groupId, userId) {
            try {
                const response = await axios.delete(`/groups/remove_user/${groupId}/${userId}`);

                // Update store data - remove user from group_users
                if (this.currentWorkspace?.workspace_groups) {
                    const groupIndex = this.currentWorkspace.workspace_groups.findIndex(
                        (group) => group.id === groupId,
                    );

                    if (
                        groupIndex !== -1 &&
                        this.currentWorkspace.workspace_groups[groupIndex].group_users
                    ) {
                        this.currentWorkspace.workspace_groups[groupIndex].group_users =
                            this.currentWorkspace.workspace_groups[groupIndex].group_users.filter(
                                (user) => user.user_id !== userId,
                            );
                    }
                }

                return response;
            } catch (error) {
                throw error;
            }
        },

        async revokeGroupInvite(inviteId) {
            try {
                const response = await axios.post(`/groups/decline_invite/${inviteId}`);

                // Update store data - remove invitation
                if (this.currentWorkspace?.workspace_groups) {
                    for (const group of this.currentWorkspace.workspace_groups) {
                        if (group.user_invitations) {
                            group.user_invitations = group.user_invitations.filter(
                                (invite) => invite.id !== inviteId,
                            );
                        }
                    }
                }

                return response;
            } catch (error) {
                throw error;
            }
        },

        async leaveGroup(groupId) {
            try {
                const response = await axios.delete(`/groups/leave/${groupId}`);

                // Update store data - remove current user from group_users
                const userStore = useUserStore();
                const currentUserId = userStore.get_user_id;

                if (this.currentWorkspace?.workspace_groups) {
                    const groupIndex = this.currentWorkspace.workspace_groups.findIndex(
                        (group) => group.id === groupId,
                    );

                    if (
                        groupIndex !== -1 &&
                        this.currentWorkspace.workspace_groups[groupIndex].group_users
                    ) {
                        this.currentWorkspace.workspace_groups[groupIndex].group_users =
                            this.currentWorkspace.workspace_groups[groupIndex].group_users.filter(
                                (user) => user.user_id !== currentUserId,
                            );
                    }
                }

                return response;
            } catch (error) {
                throw error;
            }
        },

        async removeInvite(invite_id) {
            let url = `/workspace/permissions/${invite_id}`;
            return axios
                .delete(url)
                .then(async (response) => {
                    await this.refreshWorkspace();
                    return response;
                })
                .catch(defaultErrorHandling());
        },

        async updateUserRole(workspaceId, user, newRole) {
            let url = `/workspace/permissions/${workspaceId}/user/${user.user.id}`;
            return axios
                .put(url, {
                    role: newRole,
                })
                .then(async (response) => {
                    await this.refreshWorkspace();
                    return response;
                })
                .catch(defaultErrorHandling());
        },

        async removeUser(workspaceId, userId) {
            let url = `/workspace/permissions/${workspaceId}/user/${userId}`;
            return axios
                .delete(url)
                .then(async (response) => {
                    await this.refreshWorkspace();
                    return response;
                })
                .catch(defaultErrorHandling());
        },

        async invitePerson({ emails, role }) {
            let url = `/workspace/permissions/${this.currentWorkspace.id}/invite`;
            return axios
                .post(url, {
                    emails,
                    role,
                })
                .then(async (response) => {
                    await this.refreshWorkspace();
                    return response;
                })
                .catch(defaultErrorHandling());
        },

        filterWorkspaceUsers(minimumRole) {
            // Get the numeric value for comparison
            const minimumRoleNumeric = WORKSPACE_ROLES[ROLE_TO_WORKSPACE_ROLES[minimumRole]];

            if (!minimumRoleNumeric) {
                console.error('Invalid role provided to filterWorkspaceUsers');
                return [];
            }

            return (
                this.currentWorkspace?.workspace_users?.filter((workspaceUser) => {
                    // Get the numeric value of the user's role for comparison
                    const userRoleNumeric =
                        WORKSPACE_ROLES[ROLE_TO_WORKSPACE_ROLES[workspaceUser.role]];

                    return userRoleNumeric >= minimumRoleNumeric;
                }) || []
            );
        },
    },
});
